<template>
  <AppContentLoader v-if="loading" />

  <aside
    v-else
    class="situational-analysis-page"
  >
    <DocumentPageTitle
      :word-count="totalWords"
      :word-limit="wordLimit"
      title="Situational Analysis"
    />

    <div
      v-for="storeItem in storeItems"
      :id="storeItem.id"
      :key="storeItem.id"
    >
      <template v-if="storeItem?.pageName">
        <div class="flex items-center justify-between mb-2">
          <h2>
            {{ storeItem.label }}
          </h2>

          <FontAwesomeIcon
            :icon="faCog"
            class="text-black text-lg cursor-pointer p-2"
            title="Section Settings"
            @click="openFormModal(storeItem)"
          />
        </div>

        <div
          v-if="planContentKey[storeItem.storeKey] && planContentKey[storeItem.storeKey]?.loadReportFromStatHub"
          class="mb-6 w-full flex flex-col gap-6"
        >
          <!-- Loop through all Page IDs associated with this specific storeItem setting -->
          <div
            v-for="(pageId) in getSettingValuesArray(storeItem.pageName)"
            :key="pageId"
            class="w-full"
          >
            <div
              :id="`${storeItem.id}-${pageId}-powerbi-element`"
              class="powerbi-container w-full h-[400px] border border-powerbi-report-border hidden"
            />

            <div
              :id="`${storeItem.id}-${pageId}-unloaded-powerbi-element`"
              class="relative w-full h-[400px] overflow-hidden border-2 border-powerbi-report-border flex items-center justify-center"
            >
              <img
                :src="placeholderPowerbiReport"
                class="w-full object-cover"
              >

              <div class="absolute inset-0 backdrop-blur-[5px] z-1" />

              <AppButton
                class="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 btn-solid btn-primary font-semibold !rounded-none !py-2 !px-4 absolute z-2 w-fit"
                @click="loadPowerBi(pageId, storeItem.id)"
              >
                Load this report
              </AppButton>
            </div>
          </div>
        </div>

        <TextEditorInput
          :id="`${storeItem.id}-editor`"
          v-model="planContentKey[storeItem.storeKey].text"
          :label="null"
          :word-limit="wordLimit"
          :total-word-count="totalWords"
          @update:word-count="handleWordCountUpdate"
        />
      </template>

      <TextEditorInput
        v-else
        :id="`${storeItem.id}-editor`"
        v-model="planContentKey[storeItem.storeKey].text"
        :label="storeItem.label"
        :word-limit="wordLimit"
        :total-word-count="totalWords"
        @update:word-count="handleWordCountUpdate"
      />

      <DocumentAnnexureUploadButton
        :store-key="`situationalAnalysis.${storeItem.storeKey}.documents`"
        class="mb-10"
      />
    </div>

    <AppModal
      v-if="formModal.type === 'settings'"
      :title="`${formModal.context.storeItem.label} Settings`"
      :disabled="formModal.processing"
      @close="formModal.close"
    >
      <template #body>
        <label class="input-field !my-2">
          <input
            v-model="formModal.data.loadReportFromStatHub"
            type="checkbox"
          >

          <span>Load Report from the National Strategic hub</span>
        </label>
      </template>

      <template #footer>
        <ButtonSubmitRow
          :processing="formModal.processing"
          @submit-clicked="saveSettings"
          @cancel-clicked="formModal.close"
        />
      </template>
    </AppModal>
  </aside>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref } from 'vue';
import { usePlanStore } from '@/store';
import { MunicipalityType } from '@/const';

import {
  DocumentAnnexureUploadButton,
  DocumentPageTitle,
} from '@/components/plan-document/components';
import { TextEditorInput } from '@/components/form-inputs';

import { AppButton, AppContentLoader, AppModal } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { useHttp, useTinyMCEWordCount, useModal, useString } from '@/composables';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import placeholderPowerbiReport from '@/assets/images/icons/placeholder-powerbi-report.webp';

import * as pbi from 'powerbi-client';
import { HttpStatusCode } from 'axios';

const planStore = usePlanStore();

const settingsDictionary = ref({});

const settingsPrefix = computed(() =>
  planStore.municipality?.type === MunicipalityType.DistrictMunicipality ? 'district' : 'municipality',
);
const loading = ref(false);
const wordLimit = 6000;

const planContentKey = computed(() => {
  return planStore.content?.situationalAnalysis || {};
});

const formModal = useModal();
const string = useString();

const storeItems = [
  { label: 'Overview of the municipality', storeKey: 'overview' },
  { label: 'Demographic profile', storeKey: 'demographicProfile', pageName: 'powerbi-pageids-demographic' },
  { label: 'Socio-economic profile', storeKey: 'socioeconomicProfile', pageName: 'powerbi-pageids-socio-economic' },
  { label: 'Economic profile', storeKey: 'economicProfile', pageName: 'powerbi-pageids-economic' },
  { label: 'Service delivery and infrastructure', storeKey: 'serviceDeliveryAndInfrastructure', pageName: 'powerbi-pageids-service-delivery-and-infrastructure' },
  { label: 'Finance and governance', storeKey: 'financeAndGovernance', pageName: 'powerbi-pageids-finance-and-governance' },
  { label: 'Environmental context', storeKey: 'environmentalContext' },
  { label: 'Overview of community needs and ward priorities', storeKey: 'communityNeedsOverview' },
].map(storeItem => ({ ...storeItem, id: string.sluggify(storeItem.label) }));

const storeItemIds = storeItems.map(s => `${s.id}-editor`);

const {
  totalWords,
  updateCount,
} = useTinyMCEWordCount(wordLimit, storeItemIds);

const handleWordCountUpdate = ({ id, count }) => {
  updateCount(id, count);
};

// Gets all values as an array, resolving the correct municipality/district prefix
const getSettingValuesArray = (key) => {
  const prefixedKey = `${settingsPrefix.value}-${key}`;

  if (!settingsDictionary.value[prefixedKey]) return [];

  return settingsDictionary.value[prefixedKey].split(',').map(s => s.trim()).filter(Boolean);
};

const loadSettings = async () => {
  loading.value = true;
  try {
    const response = await useHttp().get('/util/settings');

    if (response.statusCode === HttpStatusCode.Ok) {
      // Transform the Array of items into a Key-Value Object
      settingsDictionary.value = response.data.items.reduce((acc, item) => {
        acc[item.key] = item.values;
        return acc;
      }, {});
    }
  } catch (error) {
    console.error('Failed to load settings', error);
  } finally {
    loading.value = false;
  }
};

loadSettings();

const openFormModal = (storeItem) => {
  const currentValue = planContentKey.value[storeItem.storeKey].loadReportFromStatHub;
  formModal.open('settings', { loadReportFromStatHub: currentValue }, { storeItem: storeItem });
};

const saveSettings = () => {
  formModal.processing = true;
  const { data, context } = formModal;

  if (context.storeItem) {
    const key = context.storeItem.storeKey;
    planContentKey.value[key].loadReportFromStatHub = data.loadReportFromStatHub;
  }
  formModal.close();
};

let powerbiService = null;
const token = ref(null);

onUnmounted(() => {
  if (powerbiService) {
    const powerbiContainers = document.querySelectorAll('.powerbi-container');
    powerbiContainers.forEach(container => powerbiService.reset(container));
  }
});

// Load the report based on the specific pageId passed inside the loop
const loadPowerBi = async (pageId, storeItemId) => {
  // UI State management targeting exact iterated IDs
  const pbElement = document.getElementById(`${storeItemId}-${pageId}-powerbi-element`);
  const unloadedElement = document.getElementById(`${storeItemId}-${pageId}-unloaded-powerbi-element`);

  if (pbElement) pbElement.classList.remove('hidden');
  if (unloadedElement) unloadedElement.classList.add('hidden');

  if (!pageId) {
    console.warn(`No PowerBI Page ID provided for key: ${storeItemId}`);
    return;
  }

  if (!token.value) {
    try {
      const response = await useHttp().get('/profile/token');
      token.value = response.data;
    } catch (error) {
      console.error('Failed to fetch Power BI token:', error);
      return;
    }
  }

  // Initialize PowerBI Service
  if (!powerbiService) {
    powerbiService = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory,
    );
  }

  await nextTick();

  const containerId = `${storeItemId}-${pageId}-powerbi-element`;
  const container = document.getElementById(containerId);

  embedPowerBiReport(container, pageId);
};

const embedPowerBiReport = (container, pageName) => {
  if (!container || !token.value) return;

  // Retrieve dynamic configuration from settings
  const reportId = getSettingValuesArray('powerbi-report-id')[0];
  const lookupColumn = getSettingValuesArray('powerbi-region-lookup-selector')[0] || 'Municipality';
  const filterValue = planStore.municipality?.powerBiRegionLookupValue;

  if (!reportId) {
    console.error('PowerBI Report ID is missing from settings');
    return;
  }

  const reportLevelFilter = {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table: 'Lookup', column: lookupColumn },
    operator: 'In',
    values: [filterValue],
  };

  const embedConfig = {
    type: 'report',
    id: reportId, // Dynamic from settings
    embedUrl: token.value.embedUrl,
    accessToken: token.value.embedToken,
    tokenType: pbi.models.TokenType.Embed,
    pageName: pageName, // Inherits the individual `pageId` we passed in
    settings: {
      usePhasedEmbedding: true,
      layoutType: pbi.models.LayoutType.Custom,
      customLayout: { displayOption: pbi.models.DisplayOption.FitToWidth },
      panes: {
        filters: { expanded: false, visible: false },
        pageNavigation: { visible: false },
      },
      background: pbi.models.BackgroundType.Transparent,
    },
    filters: [reportLevelFilter],
  };

  try {
    // Reset any existing report in this container
    powerbiService.reset(container);
    // Embed new report
    powerbiService.embed(container, embedConfig);
  } catch (e) {
    console.error(`Failed to embed report in ${container.id}`, e);
  }
};

</script>
